import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { selectLocale } from 'models/localization';
import { CustomPageGroup } from 'models/types/customPage';
import getRemappedLinks from 'models/helpers/getRemappedLinks';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import regexUtil from 'utils/regexUtil';
import AppBarMenuLink from './AppBarMenuLink';

export interface ViewMoreDropdownProps {
  items?: any[];
}

const flatAllPages = (item: any) => (item.pages ? [...flatAllPages(item.pages)] : [item]);

const getFlattenedPaths = (items: any[]) => items
  .flatMap((item) => flatAllPages(item))
  .flatMap((pages) => pages.map((page) => Object.values(page.path || {})))
  .reduce((acc: any, curr: string) => {
    acc[curr] = true;
    return acc;
  }, {}) as Record<string, boolean>;

const mapItemToLinks = (
  item: CustomPageGroup,
  deviceName: any,
  locale: string,
  handleClick: (string: any) => any,
): any[] => {
  switch (item.display) {
    case 'DROPDOWN': {
      const flattenedPaths = getFlattenedPaths([item]);
      return [
        {
          type: 'nested',
          name: findAvailableLocale(item.title, locale),
          subLinks: getRemappedLinks(item.pages, deviceName, locale, handleClick),
          isActive: (location: any) => location.pathname in flattenedPaths,
        },
      ];
    }
    case 'TOPBAR':
    default:
      return getRemappedLinks(item.pages, deviceName, locale, handleClick);
  }
};

const ViewMoreDropdown = ({ items = [] }: ViewMoreDropdownProps) => {
  const { t } = useTranslation();
  const locale = useSelector(selectLocale);
  const { pageNames = {}, features, tabRegex } = useSelector((state: any) => state.eventConfigs);

  const flattenedPaths = useMemo(() => getFlattenedPaths(items), [items]);

  const isActive = useCallback((location: Location) => location.pathname in flattenedPaths, [flattenedPaths]);

  const handleClick = useCallback((key) => {
    window.trackingEventToFirebase(null, 'MainMenu_Click', 'CustomPage', key);
  }, []);

  const remappedLinks = useMemo(
    () => items.flatMap((it) => mapItemToLinks(it, 'desktop', locale, handleClick)),
    [items, locale, handleClick],
  );

  const tabNameRegex = useMemo(() => regexUtil(tabRegex), [tabRegex]);
  const getLabel = useCallback((title) => {
    if (features.includes('bilingualTab') && !!tabNameRegex) {
      const matched = title?.match(tabNameRegex);
      if (!matched) return title;
      return `${matched[1].trim()}<br/>${matched[2].trim()}`;
    }
    return title;
  }, [features, tabNameRegex]);

  return (
    <AppBarMenuLink
      showMenu
      htmlId="more"
      name={getLabel(findAvailableLocale({ [locale]: t('page.more', 'More'), ...(pageNames.more || {}) }, locale))}
      isActive={isActive}
      subLinks={remappedLinks}
    />
  );
};

export default ViewMoreDropdown;
